<template>
  <div class="symbol-editor">
    <div class="symbol-editor__row">
      <label>Tipo de símbolo</label>
      <select :value="symbol.type" @change="setType($event.target.value)">
        <option value="esriSMS">Forma simple</option>
        <option value="esriPMS">Ícono</option>
      </select>
    </div>

    <template v-if="symbol.type === 'esriSMS'">
      <div class="symbol-editor__row">
        <label>Forma</label>
        <select :value="symbol.style" @change="update({ style: $event.target.value })">
          <option v-for="shape in SHAPE_OPTIONS" :key="shape.value" :value="shape.value">{{ shape.label }}</option>
        </select>
      </div>

      <div class="symbol-editor__row">
        <label>Relleno</label>
        <input type="color" :value="fillHex" @input="setFillHex($event.target.value)">
        <input type="number" min="0" max="100" :value="fillOpacity" @input="setFillOpacity($event.target.value)">
        <span class="symbol-editor__unit">% opacidad</span>
      </div>

      <div class="symbol-editor__row">
        <label>Tamaño</label>
        <input type="number" min="1" max="64" :value="symbol.size" @input="update({ size: Number($event.target.value) })">
        <span class="symbol-editor__unit">pt</span>
      </div>

      <div class="symbol-editor__row">
        <label>Contorno</label>
        <input type="color" :value="outlineHex" @input="setOutlineHex($event.target.value)">
        <input type="number" min="0" max="10" step="0.5" :value="outlineWidth" @input="setOutlineWidth($event.target.value)">
        <span class="symbol-editor__unit">pt de ancho</span>
      </div>
    </template>

    <template v-else>
      <div class="symbol-editor__row">
        <label>Imagen</label>
        <input type="file" accept="image/*" @change="onIconSelected">
      </div>
      <p class="symbol-editor__hint">
        La imagen se reescala a un máximo de {{ MAX_ICON_PX }}×{{ MAX_ICON_PX }} píxeles y viaja
        dentro de la simbología, no como archivo.
      </p>
      <img v-if="symbol.imageData" class="symbol-editor__preview" :src="iconSrc" alt="Ícono seleccionado">
    </template>
  </div>
</template>

<script>
import { rgbToHex, hexToRgb, defaultSymbolFor } from "@/services/layers/symbology";

//Las opciones replican las formas que `symbology.js` sabe traducir a OpenLayers.
const SHAPE_OPTIONS = [
  { value: "esriSMSCircle", label: "Círculo" },
  { value: "esriSMSSquare", label: "Cuadrado" },
  { value: "esriSMSDiamond", label: "Rombo" },
  { value: "esriSMSTriangle", label: "Triángulo" },
  { value: "esriSMSCross", label: "Cruz" },
  { value: "esriSMSX", label: "Equis" },
];

//El ícono viaja como base64 en cada URL de tesela del MapServer: el techo es
//de largo de URL, no de peso de archivo.
const MAX_ICON_PX = 32;
const MAX_ICON_BASE64 = 4096;

const DEFAULT_PMS = { type: "esriPMS", imageData: "", contentType: "image/png" };

export default {
  name: "SymbologySymbolEditor",
  props: {
    symbol: { type: Object, required: true },
  },
  data() {
    return { SHAPE_OPTIONS, MAX_ICON_PX };
  },
  computed: {
    fillHex() {
      return rgbToHex(this.symbol.color);
    },
    fillOpacity() {
      const color = this.symbol.color;
      const alpha = Array.isArray(color) && color.length > 3 ? color[3] : 255;
      return Math.round((alpha / 255) * 100);
    },
    outlineHex() {
      return rgbToHex(this.symbol.outline && this.symbol.outline.color);
    },
    outlineWidth() {
      return this.symbol.outline ? this.symbol.outline.width : 0;
    },
    iconSrc() {
      return `data:${this.symbol.contentType || "image/png"};base64,${this.symbol.imageData}`;
    },
  },
  methods: {
    update(patch) {
      this.$emit("input", Object.assign({}, this.symbol, patch));
    },
    setType(type) {
      //Cambiar de tipo reemplaza el símbolo entero: las claves de esriSMS y
      //esriPMS no se solapan y arrastrarlas confundiría al servidor.
      this.$emit("input", type === "esriPMS"
        ? Object.assign({}, DEFAULT_PMS)
        : defaultSymbolFor("esriGeometryPoint"));
    },
    setFillHex(hex) {
      const alpha = Array.isArray(this.symbol.color) && this.symbol.color.length > 3 ? this.symbol.color[3] : 255;
      this.update({ color: hexToRgb(hex).concat(alpha) });
    },
    setFillOpacity(percent) {
      const alpha = Math.round((Math.max(0, Math.min(100, Number(percent) || 0)) / 100) * 255);
      this.update({ color: hexToRgb(this.fillHex).concat(alpha) });
    },
    setOutlineHex(hex) {
      this.update({
        outline: Object.assign({ type: "esriSLS", style: "esriSLSSolid", width: 1 }, this.symbol.outline, {
          color: hexToRgb(hex).concat(255),
        }),
      });
    },
    setOutlineWidth(width) {
      this.update({
        outline: Object.assign({ type: "esriSLS", style: "esriSLSSolid", color: [0, 0, 0, 255] }, this.symbol.outline, {
          width: Number(width) || 0,
        }),
      });
    },
    onIconSelected(event) {
      const file = event.target.files && event.target.files[0];
      //Permite volver a elegir el mismo archivo después de un rechazo.
      event.target.value = "";
      if (!file) return;

      const reader = new FileReader();
      reader.onload = () => {
        const image = new Image();
        image.onload = () => this.storeResizedIcon(image);
        image.onerror = () => this.$emit("error", "No fue posible leer la imagen seleccionada.");
        image.src = reader.result;
      };
      reader.onerror = () => this.$emit("error", "No fue posible leer la imagen seleccionada.");
      reader.readAsDataURL(file);
    },
    storeResizedIcon(image) {
      const scale = Math.min(1, MAX_ICON_PX / Math.max(image.width, image.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);

      const imageData = canvas.toDataURL("image/png").split(",")[1];
      if (imageData.length > MAX_ICON_BASE64) {
        this.$emit("error", `El ícono ocupa ${imageData.length} caracteres en base64 y el máximo es ` +
          `${MAX_ICON_BASE64}. Use una imagen con menos detalle o menos colores.`);
        return;
      }
      this.$emit("input", { type: "esriPMS", imageData: imageData, contentType: "image/png" });
    },
  },
};
</script>

<style lang="scss" scoped>
.symbol-editor__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;

  label {
    min-width: 8rem;
    margin: 0;
  }

  input[type="number"] {
    width: 5rem;
  }

  input[type="color"] {
    width: 3rem;
    padding: 0;
  }
}

.symbol-editor__unit {
  color: var(--color-ghost);
  font-size: 0.82rem;
}

.symbol-editor__hint {
  color: var(--color-ghost);
  font-size: 0.82rem;
  margin: 0 0 0.4rem;
}

.symbol-editor__preview {
  border: 1px solid var(--color-line);
  padding: 0.25rem;
}
</style>
